import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Plus, Trash2, Check, X, Eye, EyeOff, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingRow, EmptyState } from "./shared/ui";
import { CoverageAdmin } from "./CoverageAdmin";
import { Switch } from "@/components/ui/switch";
import { resetActiveLocations } from "@/components/ScopePicker";

// Round I — locaties zijn data: toevoegen, hernoemen, uitzetten. De lijst in de code dient
// alleen nog als eerste vulling en als terugval voor de detectie.
export function LocationsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState({});
  const [preview, setPreview] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [coverageKey, setCoverageKey] = useState(0);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/locations");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/locations", { name: newName.trim(), active: true });
      toast.success(`Locatie "${newName.trim()}" toegevoegd.`);
      resetActiveLocations();
      setNewName("");
      await load();
      setCoverageKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const saveRename = async (row) => {
    const next = (renaming[row.id] || "").trim();
    if (!next || next === row.name) {
      setRenaming((p) => { const n = { ...p }; delete n[row.id]; return n; });
      return;
    }
    setBusyId(row.id);
    try {
      await api.patch(`/admin/locations/${row.id}`, { name: next });
      toast.success(`Locatie hernoemd naar "${next}".`);
      resetActiveLocations();
      setRenaming((p) => { const n = { ...p }; delete n[row.id]; return n; });
      await load();
      setCoverageKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (row) => {
    setBusyId(row.id);
    try {
      await api.patch(`/admin/locations/${row.id}`, { active: !row.active });
      resetActiveLocations();
      await load();
      setCoverageKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  // Round L — a location that does not count gets no column in Dekking and is not in "N van M".
  const toggleCoverage = async (row) => {
    setBusyId(row.id);
    try {
      const next = !(row.counts_for_coverage !== false);
      await api.patch(`/admin/locations/${row.id}`, { counts_for_coverage: next });
      toast.success(next ? `${row.name} telt weer mee voor dekking.` : `${row.name} telt niet meer mee voor dekking.`);
      resetActiveLocations();
      await load();
      setCoverageKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Locatie "${row.name}" definitief verwijderen?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/admin/locations/${row.id}`);
      toast.success(`Locatie "${row.name}" verwijderd.`);
      resetActiveLocations();
      await load();
      setCoverageKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const runPreview = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/locations/backfill", { confirm: false });
      setPreview(data);
      if (!data.matched) toast.success("Er zijn geen memo's zonder locatie van de spreker.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBackfilling(false);
    }
  };

  const runApply = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/locations/backfill", { confirm: true });
      setPreview(null);
      toast.success(`${data.applied} memo${data.applied === 1 ? "" : "'s"} bijgewerkt.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBackfilling(false);
    }
  };

  const activeCount = rows.filter((r) => r.active).length;

  return (
    <div data-testid="admin-locations-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">LOCATIES</div>
        </div>
        <div className="font-mono text-sm text-tw-yellow" data-testid="admin-locations-count">
          {activeCount}
          <span className="text-tw-muted"> / {rows.length}</span>
        </div>
      </div>

      <p className="text-xs text-tw-muted mb-4 max-w-xl leading-relaxed">
        Vestigingen van TW1. Actieve locaties vullen de keuzelijst bij een teamlid, het locatiefilter
        en de automatische herkenning van plaatsnamen in een memo. Uitzetten kan altijd; verwijderen
        alleen zolang geen teamlid en geen memo eraan hangt.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) create(); }}
          placeholder="Nieuwe locatie (bv. Tilburg)"
          data-testid="admin-location-new-input"
          className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
        />
        <Button
          onClick={create}
          disabled={creating || newName.trim().length < 2}
          data-testid="admin-location-add"
          className="h-10 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4 disabled:opacity-50 shrink-0"
        >
          {creating ? <Loader2 className="animate-spin" size={14} /> : (<><Plus size={12} className="mr-1" strokeWidth={3} /> Toevoegen</>)}
        </Button>
      </div>

      {loading ? (
        <LoadingRow />
      ) : rows.length === 0 ? (
        <EmptyState label="Nog geen locaties" hint="Voeg je eerste vestiging toe." testId="admin-locations-empty" />
      ) : (
        <div className="space-y-2" data-testid="admin-locations-list">
          {rows.map((row) => {
            const isRenaming = renaming[row.id] !== undefined;
            const canDelete = row.user_count === 0 && row.memo_count === 0;
            return (
              <article
                key={row.id}
                data-testid={`admin-location-${row.id}`}
                className={`bg-tw-surface border border-tw p-3 flex items-center justify-between gap-3 ${row.active ? "" : "opacity-60"}`}
              >
                <div className="min-w-0 flex-1">
                  {isRenaming ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={renaming[row.id]}
                        onChange={(e) => setRenaming((p) => ({ ...p, [row.id]: e.target.value }))}
                        data-testid={`admin-location-rename-input-${row.id}`}
                        className="bg-tw-black border-tw text-white h-8 w-48 rounded-none focus-visible:ring-0 focus:border-tw-yellow text-xs"
                      />
                      <button onClick={() => saveRename(row)} data-testid={`admin-location-rename-save-${row.id}`} className="text-tw-yellow hover:text-white" aria-label="Naam opslaan">
                        <Check size={14} strokeWidth={3} />
                      </button>
                      <button onClick={() => setRenaming((p) => { const n = { ...p }; delete n[row.id]; return n; })} className="text-tw-muted hover:text-white" aria-label="Annuleer">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRenaming((p) => ({ ...p, [row.id]: row.name }))}
                      data-testid={`admin-location-name-${row.id}`}
                      className="text-white text-sm font-bold uppercase tracking-wider hover:text-tw-yellow transition-colors"
                    >
                      {row.name}
                    </button>
                  )}
                  <div className="mt-1 font-mono text-[10px] text-tw-muted flex items-center gap-2 flex-wrap">
                    {!row.active && <span className="border border-tw px-1.5 py-0.5 uppercase tracking-widest">(inactief)</span>}
                    <span className="inline-flex items-center gap-1"><Users size={10} /> {row.user_count} {row.user_count === 1 ? "teamlid" : "teamleden"}</span>
                    <span>· {row.memo_count} memo{row.memo_count === 1 ? "" : "'s"} van hier</span>
                    <span>· {row.spoken_count} memo{row.spoken_count === 1 ? "" : "'s"} hierover</span>
                  </div>
                  <label className="mt-2 inline-flex items-center gap-2 cursor-pointer select-none">
                    <Switch checked={row.counts_for_coverage !== false} onCheckedChange={() => toggleCoverage(row)}
                      disabled={busyId === row.id} data-testid={`admin-location-coverage-${row.id}`}
                      className="data-[state=checked]:bg-tw-yellow data-[state=unchecked]:bg-tw-black border border-tw" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted">Telt mee voor dekking</span>
                  </label>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleActive(row)}
                    disabled={busyId === row.id}
                    data-testid={`admin-location-toggle-${row.id}`}
                    title={row.active ? "Uitzetten" : "Aanzetten"}
                    aria-label={row.active ? "Uitzetten" : "Aanzetten"}
                    className="p-1 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40"
                  >
                    {busyId === row.id ? <Loader2 size={13} className="animate-spin" /> : row.active ? <Eye size={13} strokeWidth={2.5} /> : <EyeOff size={13} strokeWidth={2.5} />}
                  </button>
                  <button
                    onClick={() => remove(row)}
                    disabled={!canDelete || busyId === row.id}
                    data-testid={`admin-location-delete-${row.id}`}
                    title={canDelete ? "Verwijderen" : "Nog in gebruik — zet de locatie uit in plaats van verwijderen"}
                    className="p-1 text-tw-muted hover:text-tw-red transition-colors disabled:opacity-30 disabled:hover:text-tw-muted"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && rows.length > 0 && (
        <p className="mt-3 text-xs text-tw-muted max-w-xl leading-relaxed" data-testid="admin-locations-coverage-hint">
          "Telt mee voor dekking" uit = geen vestiging, zoals het hoofdkantoor. Die locatie krijgt geen kolom in
          Dekking en telt niet mee in "N van M". Wie daar werkt, kan nog steeds voor elke locatie antwoorden; zijn
          memo's gelden dan standaard overal.
        </p>
      )}

      <div className="mt-8 pt-6 border-t border-tw" data-testid="admin-locations-backfill">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted mb-2">LOCATIE INVULLEN OP BESTAANDE MEMO'S</div>
        <p className="text-xs text-tw-muted mb-4 max-w-xl leading-relaxed">
          Memo's van vóór deze week hebben nog geen locatie van de spreker. Hiermee vul je die in één
          keer aan. Let op: dit gaat ervan uit dat iemand destijds op zijn huidige locatie werkte. Wie meer
          locaties heeft, krijgt zijn standaardlocatie (de eerste) ingevuld.
          Memo's die al een locatie hebben blijven ongemoeid, en de genoemde locaties en het transcript
          worden nooit aangepast.
        </p>
        <Button
          onClick={runPreview}
          disabled={backfilling}
          data-testid="admin-locations-backfill-preview-btn"
          className="h-9 rounded-none bg-transparent border border-tw-yellow text-tw-yellow hover:bg-tw-yellow hover:text-black uppercase tracking-[0.2em] text-[10px] font-bold px-4 disabled:opacity-50"
        >
          {backfilling && !preview ? <Loader2 className="animate-spin" size={14} /> : "Locatie invullen op bestaande memo's"}
        </Button>

        {preview && (
          <div className="mt-4 border border-tw-yellow/40 bg-tw-yellow/5 p-3" data-testid="admin-locations-backfill-preview">
            <p className="text-sm text-white mb-2" data-testid="admin-locations-backfill-count">
              {preview.matched === 0
                ? "Er zijn geen memo's zonder locatie van de spreker."
                : `${preview.matched} memo${preview.matched === 1 ? "" : "'s"} krijgt een locatie. Nog niets opgeslagen.`}
            </p>
            {preview.people?.map((p, i) => (
              <div key={`${p.user_name}-${i}`} className="border-t border-tw py-2 font-mono text-[11px] text-tw-muted" data-testid={`admin-locations-backfill-person-${i}`}>
                {p.user_name} → <span className="text-tw-yellow">{p.location}</span> · {p.memos} memo{p.memos === 1 ? "" : "'s"}
              </div>
            ))}
            {preview.matched > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  onClick={runApply}
                  disabled={backfilling}
                  data-testid="admin-locations-backfill-apply-btn"
                  className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4 disabled:opacity-50"
                >
                  {backfilling ? <Loader2 className="animate-spin" size={14} /> : "Definitief invullen"}
                </Button>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  data-testid="admin-locations-backfill-cancel"
                  className="h-9 px-3 border border-tw text-tw-muted hover:text-white transition-colors font-mono text-[10px] uppercase tracking-[0.2em]"
                >
                  Annuleren
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <CoverageAdmin refreshKey={coverageKey} />
    </div>
  );
}
